/**
 * Done. / ChiroCPD — StatCard Feature Component
 *
 * Dashboard metric card. Shows label, value, unit, and optional health status.
 */

import { cx } from '@/lib/utils';
import type { HealthStatus } from '@/lib/types';

interface StatCardProps {
  label: string;
  value: number | string;
  unit: string;
  status?: HealthStatus;
  description?: string;
}

const STATUS_ACCENT: Record<HealthStatus, string> = {
  healthy:   'border-t-[var(--color-done-500)]',
  warning:   'border-t-[var(--color-cpd-amber)]',
  exhausted: 'border-t-[var(--color-success-500)]',
};

export function StatCard({
  label,
  value,
  unit,
  status = 'healthy',
  description,
}: StatCardProps): React.ReactElement {
  return (
    <div
      className={cx(
        'rounded-[var(--radius-md)] border-t-4 bg-[var(--surface-base)] p-4 shadow-sm',
        STATUS_ACCENT[status],
      )}
    >
      <p className="text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
        {label}
      </p>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-3xl font-bold text-[var(--text-primary)]">{value}</span>
        <span className="text-sm text-[var(--text-muted)]">{unit}</span>
      </div>
      {description && (
        <p className="mt-1 text-xs text-[var(--text-secondary)]">{description}</p>
      )}
    </div>
  );
}
